import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  DxDataGridModule,
  DxDropDownBoxComponent,
  DxFormModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxTreeViewComponent,
  DxValidatorModule,
} from 'devextreme-angular';
import { DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { AppGlobals } from 'src/app/app.globals';
import { AreaType } from 'src/app/interfaces/entities/project-params';
import { TemplatesService } from 'src/app/services/templates.service';
import { ConfigService } from 'src/app/services/config.service';
import { mapConfigurationTree } from 'src/app/helpers/configuration-tree-mapper';
import { MappedConfigTreeNode, TaskTypeEnum } from '../../../project-structure-config/project-task-config/datatypes-taskconfig';
import dxValidator from 'devextreme/ui/validator';
import { ClickEvent } from 'devextreme/ui/button';

@Component({
    selector: 'app-section-area-types',
    templateUrl: './area-types.component.html',
    styleUrls: ['./area-types.component.css'],
    imports: [
        CommonModule,
        GpdButtonComponent,
        DxFormModule,
        DxTextBoxModule,
        DxDataGridModule,
        SvgIconComponent,
        DxValidatorModule,
        DxDropDownBoxComponent,
        DxTreeViewComponent,
        DxSelectBoxModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SectionAreaTypesComponent implements OnInit {
  editData: {
    id: number | null;
    name: string;
    sizeTaskConfigId?: number | null;
    sizeUnit?: string;
  } | null = null;

  areaTypes: AreaType[] = [];

  // Task picker, only offered while creating a type - see onSave.
  configTree: MappedConfigTreeNode[] = [];
  private nodesById = new Map<string, MappedConfigTreeNode>();

  // Non-site, non-archived tasks - the same pool the task picker offers, kept flat for the size
  // task select box (see refreshSizeTaskOptions).
  allTasks: any[] = [];

  // A plain field, not a method bound in the template: devextreme-angular's _setOption calls
  // instance.option('items', ...) with no equality guard, so a fresh array on every
  // change-detection pass (typing in Name/Unit, ticking the tree) makes the select box rebuild
  // its DataSource and lose focus/search state. refreshSizeTaskOptions() is called explicitly
  // wherever the underlying data can change.
  sizeTaskOptions: { id: number; label: string }[] = [];

  // The authoritative selection, holding leaf task nodes only. Deliberately NOT read back from
  // the widget: dxTreeView rebuilds its selected-keys array from the search-FILTERED node
  // structure, so getSelectedNodeKeys() reports only what the active search term leaves visible.
  // Assigning that wholesale would silently drop every task ticked under an earlier term.
  selectedTaskNodeIds: string[] = [];
  private restoringSelection = false;

  validateUniqueName = (e: { value: string }) => {
    
    if (!e.value) return true;

    const normalizedValue = e.value.trim().toLowerCase();
    const isDuplicate = this.areaTypes.some(
      type => type.id !== this.editData?.id && type.name.trim().toLowerCase() === normalizedValue
    );
    
    return !isDuplicate;
  }

  constructor(
    private templateService: TemplatesService,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    await this.loadAreaTypes();
  }

  async loadAreaTypes() {
    try {
      const result = await this.templateService.getAreaTypes();

      if (!result.hasError) {
        this.areaTypes = result.areaTypes;
      } else {
        AppGlobals.ShowErrorMessage(result.feedback);
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Failed to load area types');
    }
    this.cdr.markForCheck();
  }

  async loadConfigTree() {
    // Reloaded on every Add rather than cached: task configs change in other tabs of the same
    // session, and picking from a stale list would attach ids that no longer exist. Two GETs on
    // a click an admin makes rarely.
    try {
      const [projectConfiguration, tasks] = await Promise.all([
        this.configService.getProjectConfiguration(),
        this.templateService.getProjectTaskConfigList({}),
      ]);

      if (projectConfiguration.hasError || tasks.hasError) {
        AppGlobals.ShowErrorMessage(projectConfiguration.feedback || tasks.feedback);
        return;
      }

      // Site tasks get every area type automatically, archived ones should not be revived -
      // neither belongs in a picker of tasks to add.
      this.allTasks = tasks.tasks.filter(task => !task.siteTask && !task.archived);
      this.configTree = mapConfigurationTree(projectConfiguration.phases, this.allTasks);
      this.nodesById = new Map(this.configTree.map(node => [node.nodeId, node]));
      this.refreshSizeTaskOptions();
    } catch (error) {
      AppGlobals.ShowErrorMessage('Failed to load project tasks');
    }
    this.cdr.markForCheck();
  }

  async onAdd() {
    this.editData = {
      id: null,
      name: '',
      sizeTaskConfigId: null,
      sizeUnit: '',
    };
    this.selectedTaskNodeIds = [];
    this.refreshSizeTaskOptions();
    await this.loadConfigTree();
  }

  // The API rejects anything else (WI 3211): a number task this area type actually uses. On create
  // the links do not exist yet, so the ticked tasks stand in for them.
  private refreshSizeTaskOptions(): void {
    if (!this.editData) {
      this.sizeTaskOptions = [];
      return;
    }
    const eligible = this.editData.id
      ? (task: any) => task.areaTypeIds?.includes(this.editData!.id)
      : (task: any) => this.selectedTaskConfigIds().includes(task.id);
    this.sizeTaskOptions = this.allTasks
      .filter(task => task.type === TaskTypeEnum.Number && eligible(task))
      .map(task => ({ id: task.id, label: `${task.nr} | ${task.title}` }));

    // Create only: the ticked tasks are the only "linked" source until the type is saved, so an
    // unticked pick has to fall back to null or the API rejects the save. On edit, options come
    // from areaTypeIds instead, and a mismatch there can be a link that migrated elsewhere -
    // clearing it would silently wipe a valid size task on the next save.
    if (
      !this.editData.id &&
      this.editData.sizeTaskConfigId != null &&
      !this.sizeTaskOptions.some(o => o.id === this.editData!.sizeTaskConfigId)
    ) {
      this.editData.sizeTaskConfigId = null;
    }
  }

  onTaskTreeReady(e: DxTreeViewTypes.ContentReadyEvent) {
    // Fires on open and again on every search keystroke - the widget drops its own selection on a
    // searchValue change (tree_view.search.js calls _removeSelection when checkboxes and recursive
    // selection are on), so the stored set has to be reapplied each time. Keys the search is
    // hiding are simply not found and stay stored until the filter widens again.
    // The flag is load-bearing: each selectItem fires onTaskSelectionChanged, whose reconcile
    // would read the not-yet-restored keys as unticked and drop them.
    this.restoringSelection = true;
    try {
      this.selectedTaskNodeIds.forEach(nodeId => e.component.selectItem(nodeId));
    } finally {
      this.restoringSelection = false;
    }
  }

  onTaskSelectionChanged(e: DxTreeViewTypes.ItemSelectionChangedEvent) {
    if (this.restoringSelection) return;

    // Reconcile only the slice the search leaves visible; getNodes() is that filtered tree, and
    // anything outside it keeps whatever the admin ticked earlier.
    const visibleKeys = new Set<string>();
    const collect = (nodes: { key: string; children?: any[] }[]) =>
      nodes.forEach(node => {
        visibleKeys.add(node.key);
        collect(node.children ?? []);
      });
    collect(e.component.getNodes() as any);

    const next = new Set(this.selectedTaskNodeIds.filter(nodeId => !visibleKeys.has(nodeId)));
    (e.component.getSelectedNodeKeys() as string[]).forEach(nodeId => next.add(nodeId));

    // Groups never enter the set: phases, milestones, task packages and group tasks all derive
    // their area types from their children (see UpdateParents in the API), and recursive
    // selection would otherwise put them in here just because their children are ticked.
    this.selectedTaskNodeIds = [...next].filter(nodeId => this.nodesById.get(nodeId)?.type !== TaskTypeEnum.Group);
    this.refreshSizeTaskOptions();
    this.cdr.markForCheck();
  }

  async onEdit(item: AreaType) {
    this.selectedTaskNodeIds = [];
    this.editData = {
      id: item.id,
      name: item.name,
      sizeTaskConfigId: item.sizeTaskConfigId ?? null,
      sizeUnit: item.sizeUnit ?? '',
    };
    this.refreshSizeTaskOptions();
    // Editing needs the task list too, for the size task select box.
    await this.loadConfigTree();
  }

  onCancel() {
    this.editData = null;
    this.selectedTaskNodeIds = [];
    this.refreshSizeTaskOptions();
  }

  async onSave(e: ClickEvent) {
    try {
      var validationResult = e.validationGroup.validate();
      
      
      if (!validationResult.isValid) {
        return;
      }
      const result = await this.templateService.writeProjectAreaType({
        areaType: {
          id: this.editData.id,
          name: this.editData.name,
          isDeleted: false,
          sizeTaskConfigId: this.editData.sizeTaskConfigId ?? null,
          sizeUnit: (this.editData.sizeUnit ?? '').trim() || null,
        },
        // Groups come along automatically once one of their tasks carries the type. The API
        // rejects a non-empty list on an update, so only a create sends one.
        taskConfigIds: this.editData.id ? [] : this.selectedTaskConfigIds(),
        // Tells the API this save actually means to set the size fields - an old UI bundle that
        // predates this feature never sends the flag, so a plain rename cannot null them out.
        sizeFieldsIncluded: true,
      });

      if (!result.hasError) {
        this.editData = null;
        this.selectedTaskNodeIds = [];
        await this.loadAreaTypes();
      } else {
        AppGlobals.ShowErrorMessage(result.feedback);
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Failed to save Area Type');
    }
    this.cdr.markForCheck();
  }

  private selectedTaskConfigIds(): number[] {
    return this.selectedTaskNodeIds
      .map(nodeId => this.nodesById.get(nodeId)?.id)
      .filter((id): id is number => id != null);
  }

  async deleteAreaType(item: AreaType) {
    try {

      const result = await this.templateService.writeProjectAreaType({
        areaType: {
          ...item,
          isDeleted: true,
        },

      });

      if (!result.hasError) {
        await this.loadAreaTypes();
      } else {
        AppGlobals.ShowErrorMessage(result.feedback);
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Failed to delete area type');
    }
  }
}
